<script setup lang="ts">
import { computed, ref, type PropType, toRef } from 'vue'
import type { Field } from '@/components/form/type/type.ts'
import CustomHelpTextToolTip from '@/components/form/CustomHelpTextToolTip.vue'

const props = defineProps({
  modelValue: { type: Object, required: true },
  field: { type: Object as PropType<Field>, required: true },
  readonly: { type: Boolean, required: false, default: false },
  descriptions: {
    type: Array as
      | PropType<Record<string, { description: string; helpText: string; message: string }>[]>
      | undefined,
    required: false,
    default: () => []
  },
  title: { type: String, required: false, default: '' },
  subtitle: { type: String, required: false, default: '' }
})

const emit = defineEmits<{
  'update:modelValue': [Record<string, any>]
}>()

const modelValue = toRef(props, 'modelValue')
const field = toRef(props, 'field')
const title = toRef(props, 'title')
const subtitle = toRef(props, 'subtitle')

const itemKey = computed(() => field.value.parameter)

const value = computed({
  get() {
    return props.modelValue[itemKey.value]
  },
  set(newValue) {
    modelValue.value[itemKey.value] = newValue
    emit('update:modelValue', props.modelValue)
  }
})

const choices = computed(() => field.value.choices ?? [])
const warning = ref(true)

const descriptionMap = computed(() => {
  const map: Record<string, { description: string; helpText: string; message: string }> = {}
  props.descriptions?.forEach((entry) => {
    Object.assign(map, entry)
  })
  return map
})
function validateSelection() {
  if (!value.value) {
    warning.value = true
  } else {
    warning.value = false
  }
}
console
onMounted(() => {
  if (value.value) {
    warning.value = false
  }
})
</script>

<template>
  <div v-if="field && choices.length > 0">
    <v-card-title
      v-if="title"
      class="header-font d-flex justify-space-between align-center"
      :style="{ marginLeft: '-20px' }"
    >
      {{ title }}
    </v-card-title>
    <div v-if="subtitle" class="subtitle-text">{{ subtitle }}</div>
    <div v-if="warning" class="warning-text">Please select one of the options.</div>
    <v-radio-group
      v-model="value"
      :mandatory="true"
      class="custom-radio-group"
      @change="validateSelection"
    >
      <div
        v-for="choice in choices"
        :key="choice.value"
        class="radio-option mb-4 d-flex flex-column"
      >
        <div class="label-area d-flex align-center ml-1">
          <CustomHelpTextToolTip
            v-if="descriptionMap[choice.display_name]?.helpText"
            :text="descriptionMap[choice.display_name].helpText"
            class="mr-2"
          />
          <span class="px-2 v-input">{{ choice.display_name }}</span>
          <v-radio
            :value="choice.value"
            color="primary"
            class="radio-align mr-5"
            :disabled="readonly"
          />
        </div>
        <v-row class="gap-0 mt-0">
          <v-col
            v-if="descriptionMap[choice.display_name]?.description"
            class="option-description"
            cols="12"
            style="padding: 0"
          >
            {{ descriptionMap[choice.display_name].description }}
          </v-col>
          <v-col class="option-message" cols="12" style="padding: 0">
            <span
              v-if="value === choice.value && descriptionMap[choice.display_name]?.message"
              class="text-body-2"
            >
              {{ descriptionMap[choice.display_name].message }}
            </span>
            <span v-else style="visibility: hidden">
              {{ descriptionMap[choice.display_name]?.message || ' ' }}
            </span>
          </v-col>
        </v-row>
      </div>
    </v-radio-group>
  </div>
</template>

<style scoped>
.custom-radio-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-left: -1px;
}

.v-input :deep(.v-input__control) {
  margin-left: 20px;
}

.v-input :deep(.v-input__details) {
  display: none;
}

.label-area {
  min-width: 220px;
  display: flex;
  align-items: center;
}

.radio-align {
  margin-left: 16px;
}

.radio-option {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 0;
  padding-bottom: 0;
}

.option-description {
  margin: 0;
  padding: 0;
  font-size: 1rem;
  color: black;
  margin-left: 20px;
  white-space: wrap;
  overflow-wrap: normal;
  word-wrap: normal;
  width: calc(100% - 20px);
  display: block;
}

.warning-text {
  color: red;
  font-size: 0.9rem;
  margin-top: 8px;
  margin-left: -3px;
}

.option-message {
  margin: 0;
  padding: 0;
  font-size: 0.9rem;
  font-style: italic;
  margin-left: 20px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.v-row.no-gap {
  margin: 0;
  padding: 0;
  row-gap: 0;
}

.subtitle-text {
  font-size: 0.9rem;
  color: gray;
  margin-top: -8px;
  white-space: nowrap;
  margin-left: -3px;
}
</style>
